import { client } from "../api/client.js";
import { setAuthInProgress } from "../router.js";
import { track } from "../utils/analytics.js";
import { getAttribution } from "../utils/attribution.js";
import PLANS from "../data/plans.json";

// ── T-03: invisible Cloudflare Turnstile ───────────────────────────
// Rendered only when the server exposes a site key (turnstileSiteKey on
// /api/auth/google/config). interaction-only: no visible widget unless
// Cloudflare decides a challenge is needed. If the script never loads
// (blocked), the form submits without a token and the server answers
// with a clear 400 — no silent client-side dead end.
let _turnstileToken = '';
let _turnstileWidgetId = null;

function initTurnstile(el, siteKey) {
    if (!siteKey) return;
    const container = el.querySelector('#turnstile-container');
    if (!container) return;
    _turnstileToken = '';
    function render() {
        if (!window.turnstile) return false;
        try {
            _turnstileWidgetId = window.turnstile.render(container, {
                sitekey: siteKey,
                appearance: 'interaction-only',
                callback: (t) => { _turnstileToken = t; },
                'expired-callback': () => {
                    _turnstileToken = '';
                    try { window.turnstile.reset(_turnstileWidgetId); } catch (_) { /* widget gone */ }
                },
                'error-callback': () => { _turnstileToken = ''; },
            });
        } catch (_) { /* double-render or removed container */ }
        return true;
    }
    if (render()) return;
    if (!document.getElementById('turnstile-script')) {
        const s = document.createElement('script');
        s.id = 'turnstile-script';
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = render;
        document.head.appendChild(s);
    } else {
        let attempts = 0;
        const iv = setInterval(() => {
            attempts++;
            if (render() || attempts > 20) clearInterval(iv);
        }, 250);
    }
}

function getTurnstileToken() { return _turnstileToken; }

// Shared payload extras for register-shaped requests (password + Google).
function signupExtras() {
    const extras = {};
    const attribution = getAttribution();
    if (attribution) extras.attribution = attribution;
    const token = getTurnstileToken();
    if (token) extras.turnstile_token = token;
    return extras;
}

function requireEl(sel, parent = document) {
    const el = parent.querySelector(sel);
    if (!el) throw new Error(`UI_CONTRACT_VIOLATION: missing ${sel}`);
    return el;
}

function googleDivider() {
    return `<div class="auth-divider" style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
        <hr style="flex:1;border:none;border-top:1px solid var(--border);">
        <span style="font-size:12px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;">or</span>
        <hr style="flex:1;border:none;border-top:1px solid var(--border);">
    </div>`;
}

function initGoogleButton(el, errorEl, googleClientId) {
    function doInit() {
        if (!window.google?.accounts?.id) return false;
        window.google.accounts.id.initialize({
            client_id: googleClientId,
            ux_mode: 'popup',
            callback: async (response) => {
                errorEl.classList.add("hide");
                setAuthInProgress(true);
                try {
                    const res = await client.authGoogleCallback(response.credential, signupExtras());
                    window.updateGlobalAuthState(res.user, "GOOGLE_LOGIN");
                    setAuthInProgress(false);
                    window.location.hash = "#home";
                } catch (err) {
                    errorEl.textContent = err.message || 'Google sign-in failed';
                    errorEl.classList.remove("hide");
                } finally {
                    setAuthInProgress(false);
                }
            }
        });
        const btnEl = el.querySelector("#google-signin-btn");
        if (btnEl) {
            // Phase 36-G: width must be a NUMBER (200-400 per Google's
            // Identity Services spec). Was passed as "100%" string before,
            // which Google rejected → button rendered at default ~200px.
            // Phase 36-K: bumped 380 → 384 to match the card body content
            // width EXACTLY (card max-width 480 - padding 48*2 = 384).
            // Otherwise the iframe is 384 wide but Google renders the
            // button content at 380 → 4px right-edge gap inside the iframe
            // that reads as the button being "cut off" or off-center.
            // T-16: on narrow viewports (390px phones) the fixed 384 width
            // overflowed the card. Fit to the container, clamped to Google's
            // allowed 200-400 range.
            const containerW = btnEl.parentElement ? btnEl.parentElement.clientWidth : 0;
            const gsiWidth = Math.max(200, Math.min(384, containerW || 384));
            window.google.accounts.id.renderButton(
                btnEl,
                { theme: "outline", size: "large", width: gsiWidth, text: "signin_with" }
            );
        }
        return true;
    }
    // Try immediately, or wait for GIS script to load
    if (!doInit()) {
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            if (doInit() || attempts > 20) clearInterval(interval);
        }, 250);
    }
}

export class LoginPage {
    async mount(el) {
        let googleEnabled = false;
        let googleClientId = '';
        let turnstileSiteKey = '';
        try {
            const config = await client.authGoogleConfig();
            googleEnabled = config.enabled;
            googleClientId = config.clientId;
            turnstileSiteKey = config.turnstileSiteKey || '';
        } catch (e) { /* Google not configured */ }

        el.innerHTML = `
            <div class="auth-stripe">
                <!-- Top-left wordmark — pixel-matched to heistbrain.com's
                     <a class="nav-logo"> so the lockup appears to stay in
                     place when navigating from the landing page. -->
                <a href="https://heistbrain.com" class="auth-stripe__brand" aria-label="Heist — back to homepage"><img class="auth-stripe__brand-mark" src="/img/heist-mark.svg" alt="" />Heist<span class="auth-stripe__brand-dot">.</span></a>

                <!-- Vibrant brushstroke graphic flowing behind everything -->
                <div class="auth-stripe__graphic" aria-hidden="true">
                    <div class="auth-stripe__graphic-inner">
                        <div class="brush brush--1"></div>
                        <div class="brush brush--2"></div>
                        <div class="brush brush--3"></div>
                        <div class="brush brush--4"></div>
                    </div>
                </div>

                <!-- Centered white card -->
                <div class="auth-stripe__card">
                    <div class="auth-stripe__card-body">
                        <h1>Sign in to your account</h1>

                        <form id="login-form" novalidate>
                            <div class="field">
                                <label for="login-email-input">Email</label>
                                <input type="email" id="login-email-input" name="email" autocomplete="email" data-testid="login-email" required />
                            </div>
                            <div class="field">
                                <div class="auth-stripe__field-row">
                                    <label for="login-password-input">Password</label>
                                    <a href="#" class="auth-stripe__forgot" id="forgot-link">Forgot your password?</a>
                                </div>
                                <input type="password" id="login-password-input" name="password" autocomplete="current-password" data-testid="login-password" required />
                            </div>
                            <label class="auth-stripe__remember">
                                <input type="checkbox" id="remember-me-checkbox" />
                                Remember me on this device
                            </label>
                            <div id="login-error" class="error-msg hide"></div>
                            <button type="submit" data-testid="login-submit">Sign in</button>
                        </form>

                        ${googleEnabled ? `
                        <div class="auth-stripe__divider"><span>or</span></div>
                        <div class="auth-stripe__google" id="google-signin-container">
                            <div id="google-signin-btn"></div>
                            <div id="turnstile-container"></div>
                        </div>
                        ` : ''}

                        <div id="forgot-form" class="forgot-form hide">
                            <p style="font-size:13px;color:#647294;/* fixed: auth staging — always-white stripe card */margin: 12px 0 10px;">Enter your email to receive a reset link.</p>
                            <input type="email" id="forgot-email" placeholder="Email address">
                            <button id="forgot-submit">Send Reset Link</button>
                            <div id="forgot-msg" class="hide" style="margin-top:8px;font-size:12px;color:#10B981;/* fixed: auth staging — always-white stripe card */"></div>
                        </div>
                    </div>

                    <div class="auth-stripe__card-footer">
                        New to Heist? <a href="#signup">Create account</a>
                    </div>
                </div>

                <!-- Tiny bottom-left footer -->
                <div class="auth-stripe__footer">
                    <span>\u00a9 Heist</span>
                    <a href="https://heistbrain.com/terms" target="_blank" rel="noopener">Terms</a>
                    <a href="https://heistbrain.com/privacy" target="_blank" rel="noopener">Privacy</a>
                </div>
            </div>
        `;

        const form = requireEl("#login-form", el);
        const errorEl = requireEl("#login-error", el);

        form.addEventListener("submit", async (e) => {
            e.preventDefault();
            errorEl.classList.add("hide");
            const email = requireEl("#login-email-input", form).value;
            const password = requireEl("#login-password-input", form).value;
            const rememberMe = el.querySelector("#remember-me-checkbox")?.checked || false;

            setAuthInProgress(true);
            try {
                const res = await client.authLogin({ email, password, rememberMe });
                window.updateGlobalAuthState(res.user, "LOGIN");
                setAuthInProgress(false);
                window.location.hash = "#home";
            } catch (err) {
                errorEl.textContent = err.message;
                errorEl.classList.remove("hide");
            } finally {
                setAuthInProgress(false);
            }
        });

        // Forgot password toggle
        const forgotLink = el.querySelector('#forgot-link');
        const forgotForm = el.querySelector('#forgot-form');
        if (forgotLink && forgotForm) {
            forgotLink.addEventListener('click', (e) => {
                e.preventDefault();
                form.classList.toggle('hide');
                forgotForm.classList.toggle('hide');
            });
            el.querySelector('#forgot-submit')?.addEventListener('click', async () => {
                const email = el.querySelector('#forgot-email')?.value;
                if (!email) return;
                const btn = el.querySelector('#forgot-submit');
                btn.disabled = true;
                btn.textContent = 'Sending...';
                try {
                    await client.forgotPassword(email);
                    const msg = el.querySelector('#forgot-msg');
                    if (msg) { msg.textContent = 'If an account exists, a reset link has been sent.'; msg.classList.remove('hide'); }
                    btn.textContent = 'Sent';
                } catch (e) {
                    btn.disabled = false;
                    btn.textContent = 'Send Reset Link';
                }
            });
        }

        if (googleEnabled) {
            initGoogleButton(el, errorEl, googleClientId);
        }
        initTurnstile(el, turnstileSiteKey);
    }
    unmount() { }
}

export class ResetPasswordPage {
    async mount(el) {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const token = params.get('token') || '';

        el.innerHTML = `
            <div class="auth-container">
                <div class="auth-box">
                    <div class="auth-logo"><img class="auth-logo__mark" src="/img/heist-mark.svg" alt="" /><span class="auth-logo__word">Heist<span>.</span></span></div>
                    <h1>Reset Password</h1>
                    <p class="auth-subtitle">Enter your new password below.</p>
                    <form id="reset-form" novalidate>
                        <input type="hidden" id="reset-token" value="${token.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;')}">
                        <div class="field">
                            <label for="reset-password">New Password</label>
                            <input type="password" id="reset-password" placeholder="Min 8 characters" minlength="8" required />
                        </div>
                        <div class="field">
                            <label for="reset-confirm">Confirm Password</label>
                            <input type="password" id="reset-confirm" placeholder="Confirm password" required />
                        </div>
                        <div id="reset-error" class="error-msg hide"></div>
                        <div id="reset-success" class="hide" style="color:var(--success);font-size:13px;margin-bottom:12px;"></div>
                        <button type="submit" id="reset-submit">Reset Password</button>
                    </form>
                    <p class="auth-switch"><a href="#login">Back to Login</a></p>
                </div>
            </div>
        `;

        const form = el.querySelector('#reset-form');
        const errorEl = el.querySelector('#reset-error');
        const successEl = el.querySelector('#reset-success');

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            errorEl.classList.add('hide');
            successEl.classList.add('hide');

            const t = el.querySelector('#reset-token')?.value;
            const pw = el.querySelector('#reset-password')?.value || '';
            const confirm = el.querySelector('#reset-confirm')?.value || '';

            if (!t) { errorEl.textContent = 'Missing reset token. Use the link from your email.'; errorEl.classList.remove('hide'); return; }
            if (pw.length < 8) { errorEl.textContent = 'Password must be at least 8 characters.'; errorEl.classList.remove('hide'); return; }
            if (pw !== confirm) { errorEl.textContent = 'Passwords do not match.'; errorEl.classList.remove('hide'); return; }

            const btn = el.querySelector('#reset-submit');
            if (!btn) return;
            btn.disabled = true;
            btn.textContent = 'Resetting...';

            try {
                await client.resetPassword(t, pw);
                successEl.textContent = 'Password updated! Redirecting to login...';
                successEl.classList.remove('hide');
                setTimeout(() => { window.location.hash = '#login'; }, 2000);
            } catch (err) {
                errorEl.textContent = err.message || 'Reset failed';
                errorEl.classList.remove('hide');
                btn.disabled = false;
                btn.textContent = 'Reset Password';
            }
        });
    }
    unmount() { }
}

export class SignupPage {
    async mount(el) {
        let googleEnabled = false;
        let googleClientId = '';
        let turnstileSiteKey = '';
        try {
            const config = await client.authGoogleConfig();
            googleEnabled = config.enabled;
            googleClientId = config.clientId;
            turnstileSiteKey = config.turnstileSiteKey || '';
        } catch (e) { /* Google not configured */ }

        el.innerHTML = `
            <div class="auth-stripe">
                <!-- Top-left wordmark — pixel-matched to heistbrain.com nav-logo -->
                <a href="https://heistbrain.com" class="auth-stripe__brand" aria-label="Heist — back to homepage"><img class="auth-stripe__brand-mark" src="/img/heist-mark.svg" alt="" />Heist<span class="auth-stripe__brand-dot">.</span></a>

                <!-- Vibrant brushstroke graphic flowing behind the card -->
                <div class="auth-stripe__graphic" aria-hidden="true">
                    <div class="auth-stripe__graphic-inner">
                        <div class="brush brush--1"></div>
                        <div class="brush brush--2"></div>
                        <div class="brush brush--3"></div>
                        <div class="brush brush--4"></div>
                    </div>
                </div>

                <!-- Centered white card. --signup modifier tightens spacing so the
                     4-field form fits without scrolling. -->
                <div class="auth-stripe__card auth-stripe__card--signup">
                    <div class="auth-stripe__card-body">
                        <h1>Create your account</h1>
                        <!-- T-16: value recap — what signing up actually gets you. -->
                        <p class="auth-stripe__recap">7 days free &middot; 25 posts on us &middot; no card required</p>

                        ${googleEnabled ? `
                        <div class="auth-stripe__google auth-stripe__google--primary" id="google-signin-container">
                            <div id="google-signin-btn"></div>
                            <div id="turnstile-container"></div>
                        </div>
                        <div class="auth-stripe__divider"><span>or with email</span></div>
                        ` : ''}

                        <form id="signup-form" novalidate>
                            <div class="field">
                                <label for="signup-name-input">Full name</label>
                                <input type="text" id="signup-name-input" name="name" autocomplete="name" data-testid="signup-name" required minlength="2" />
                                <div class="field-error" id="signup-name-error"></div>
                            </div>
                            <div class="field">
                                <label for="signup-email-input">Email</label>
                                <input type="email" id="signup-email-input" name="email" autocomplete="email" data-testid="signup-email" required />
                                <div class="field-error" id="signup-email-error"></div>
                            </div>
                            <div class="field">
                                <label for="signup-password-input">Password</label>
                                <input type="password" id="signup-password-input" name="password" autocomplete="new-password" data-testid="signup-password" required minlength="8" />
                                <div class="password-strength" id="password-strength"></div>
                                <div class="field-error" id="signup-password-error"></div>
                            </div>
                            <div class="field">
                                <label for="signup-confirm-input">Confirm password</label>
                                <input type="password" id="signup-confirm-input" name="confirmPassword" autocomplete="new-password" data-testid="signup-confirm" />
                                <div class="field-error" id="signup-confirm-error"></div>
                            </div>
                            <div id="signup-error" class="error-msg hide"></div>
                            <button type="submit" data-testid="signup-submit">Create account</button>
                        </form>

                        <!-- T-16: consent line with real legal links (used to point at #help). -->
                        <p class="auth-stripe__consent">By creating an account you agree to the
                            <a href="https://heistbrain.com/terms" target="_blank" rel="noopener">Terms</a> and
                            <a href="https://heistbrain.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>.</p>
                    </div>

                    <div class="auth-stripe__card-footer">
                        Already have an account? <a href="#login">Sign in</a>
                    </div>
                </div>

                <!-- Tiny bottom-left footer -->
                <div class="auth-stripe__footer">
                    <span>© Heist</span>
                    <a href="https://heistbrain.com/terms" target="_blank" rel="noopener">Terms</a>
                    <a href="https://heistbrain.com/privacy" target="_blank" rel="noopener">Privacy</a>
                </div>
            </div>
        `;

        const form = requireEl("#signup-form", el);
        const errorEl = requireEl("#signup-error", el);
        const nameInput = requireEl("#signup-name-input", el);
        const emailInput = requireEl("#signup-email-input", el);
        const passwordInput = requireEl("#signup-password-input", el);
        const confirmInput = requireEl("#signup-confirm-input", el);
        const strengthEl = requireEl("#password-strength", el);

        // Password strength indicator
        passwordInput.addEventListener('input', () => {
            const val = passwordInput.value;
            let strength = '';
            let cls = '';
            if (val.length === 0) { strength = ''; cls = ''; }
            else if (val.length < 8) { strength = 'Too short'; cls = 'strength-weak'; }
            else if (val.length < 12 && !/[A-Z]/.test(val)) { strength = 'Weak'; cls = 'strength-weak'; }
            else if (val.length >= 12 || (/[A-Z]/.test(val) && /[0-9]/.test(val))) { strength = 'Strong'; cls = 'strength-strong'; }
            else { strength = 'Medium'; cls = 'strength-medium'; }
            strengthEl.textContent = strength;
            strengthEl.className = 'password-strength ' + cls;
        });

        // Clear field errors on input
        [nameInput, emailInput, passwordInput, confirmInput].forEach(input => {
            input.addEventListener('input', () => {
                const errEl = el.querySelector(`#${input.id.replace('-input', '-error')}`);
                if (errEl) { errEl.textContent = ''; errEl.classList.remove('visible'); }
                input.classList.remove('input-error');
            });
        });

        function showFieldError(inputEl, errorId, message) {
            const errEl = el.querySelector(`#${errorId}`);
            if (errEl) { errEl.textContent = message; errEl.classList.add('visible'); }
            inputEl.classList.add('input-error');
        }

        form.addEventListener("submit", async (e) => {
            e.preventDefault();
            errorEl.classList.add("hide");

            const name = nameInput.value.trim();
            const email = emailInput.value.trim();
            const password = passwordInput.value;
            const confirm = confirmInput.value;

            // Client-side validation
            let valid = true;

            if (!name || name.length < 2) {
                showFieldError(nameInput, 'signup-name-error', 'Name must be at least 2 characters');
                valid = false;
            }

            if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                showFieldError(emailInput, 'signup-email-error', 'Please enter a valid email address');
                valid = false;
            }

            if (!password || password.length < 8) {
                showFieldError(passwordInput, 'signup-password-error', 'Password must be at least 8 characters');
                valid = false;
            }

            // Only validate confirm if user typed something in it
            if (confirm && password !== confirm) {
                showFieldError(confirmInput, 'signup-confirm-error', 'Passwords do not match');
                valid = false;
            }

            if (!valid) return;

            setAuthInProgress(true);
            try {
                const res = await client.authRegister({ name, email, password, ...signupExtras() });
                track('signup_completed', { method: 'password' });
                window.updateGlobalAuthState(res.user, "SIGNUP");
                setAuthInProgress(false);

                if (window.location.hash !== "#home") {
                    window.location.hash = "#home";
                } else {
                    if (window.__router__) window.__router__.go("home");
                }
            } catch (err) {
                errorEl.textContent = err.message;
                errorEl.classList.remove("hide");
            } finally {
                setAuthInProgress(false);
            }
        });

        if (googleEnabled) {
            initGoogleButton(el, errorEl, googleClientId);
        }
        initTurnstile(el, turnstileSiteKey);
    }
    unmount() { }
}

export class UpgradePage {
    async mount(el) {
        track('paywall_viewed', {
            trial_expired: !!(window.__auth__ && window.__auth__.trial_expires_at &&
                new Date(window.__auth__.trial_expires_at) < new Date()),
        });
        // T-04 — server-side milestone (GA above is cookie/adblock-dependent)
        client.funnelEvent('paywall_viewed').catch(() => {});
        // T-07 — tiers come from the shared plan config (app/src/data/plans.json),
        // the same source Settings→Billing renders and the marketing pricing
        // section is diffed against. Do not hardcode plan copy here again.
        const tiers = PLANS.plans;
        let cycle = 'monthly';
        // Session B1 — this page is reachable mid-trial (nav, deep link), not
        // only at lockout. Don't tell an active-trial user their trial ended.
        const trialEnded = !!(window.__auth__ && window.__auth__.trial_expires_at &&
            new Date(window.__auth__.trial_expires_at) < new Date() && !window.__auth__.has_stripe);
        const checkSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

        // T-17 — set once the usage recap loads; re-render highlights the
        // plan that fits what the user actually did.
        let recommendedTier = null;
        const renderCards = () => tiers.map(t => `
            <div class="upgrade-card${t.popular ? ' upgrade-card--popular' : ''}${t.id === recommendedTier ? ' upgrade-card--fits' : ''}">
                ${t.id === recommendedTier ? '<div class="upgrade-card-fits-badge">Fits your usage</div>' : ''}
                <div class="upgrade-card-name">${t.name}</div>
                <div class="upgrade-card-price"><span>$${cycle === 'monthly' ? t.monthly : t.annual}</span><span>/${cycle === 'monthly' ? 'mo' : 'mo, billed yearly'}</span></div>
                <ul class="upgrade-card-features">${t.features.map(f => `<li>${checkSvg} ${f}</li>`).join('')}</ul>
                <button class="btn btn-primary upgrade-card-btn" data-tier="${t.id}">Choose ${t.name}</button>
            </div>`).join('');

        el.innerHTML = `
        <div class="upgrade-lockout">
            <div class="upgrade-lockout-inner">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary,#8B5CF6)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:12px;">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <h1 style="font-size:28px;font-weight:800;margin:0 0 8px;">${trialEnded ? 'Your trial has ended' : 'Upgrade your plan'}</h1>
                <p style="color:var(--text-muted);font-size:15px;max-width:480px;margin:0 auto 20px;">${trialEnded ? 'Pick a plan to keep creating with Heist.' : 'Keep everything you\'ve built and unlock the full toolkit.'} Cancel anytime.</p>

                <!-- T-17: the user's own results, loaded async — stays empty for accounts with no activity. -->
                <div id="upgrade-recap" class="upgrade-recap" hidden></div>

                <div style="display:inline-flex;border:1px solid var(--border);border-radius:10px;overflow:hidden;margin-bottom:8px;">
                    <button class="btn billing-cycle-btn" data-cycle="monthly" style="border:none;border-radius:10px 0 0 10px;">Monthly</button>
                    <button class="btn billing-cycle-btn" data-cycle="annual" style="border:none;border-radius:0 10px 10px 0;">Annual <span style="font-size:11px;opacity:.7;">save ~17%</span></button>
                </div>

                <div class="upgrade-lockout-cards" id="upgrade-cards">${renderCards()}</div>

                <div class="upgrade-coupon-section">
                    <p style="font-size:13px;color:var(--text-muted);margin:0 0 8px;">Have a coupon code?</p>
                    <div class="upgrade-coupon-row">
                        <input class="upgrade-coupon-input" id="coupon-input" placeholder="Enter code" maxlength="32" />
                        <button class="btn btn-primary" id="coupon-apply-btn">Apply</button>
                    </div>
                    <div class="upgrade-coupon-msg" id="coupon-msg"></div>
                </div>

                <p style="font-size:13px;color:var(--text-muted);margin-top:28px;">Need help? <a href="#help" style="color:var(--primary);">Contact support</a></p>
            </div>
        </div>`;

        // Tier buttons
        const bindTierBtns = () => {
            el.querySelectorAll('[data-tier]').forEach(btn => {
                btn.addEventListener('click', async () => {
                    btn.disabled = true;
                    btn.textContent = 'Redirecting...';
                    track('checkout_started', { tier: btn.dataset.tier, cycle });
                    try {
                        await client.createCheckout(btn.dataset.tier, cycle, window.__discountCode);
                    } catch (err) {
                        btn.disabled = false;
                        btn.textContent = `Choose ${btn.dataset.tier.charAt(0).toUpperCase() + btn.dataset.tier.slice(1)}`;
                        const msg = el.querySelector('#coupon-msg');
                        msg.textContent = err.message || 'Checkout failed';
                        msg.style.color = 'var(--danger)';
                    }
                });
            });
        };

        // Billing cycle toggle
        const cycleBtns = el.querySelectorAll('.billing-cycle-btn');
        const syncCycleUI = () => {
            cycleBtns.forEach(b => b.classList.toggle('active', b.dataset.cycle === cycle));
            el.querySelector('#upgrade-cards').innerHTML = renderCards();
            bindTierBtns();
        };
        cycleBtns.forEach(b => b.addEventListener('click', () => { cycle = b.dataset.cycle; syncCycleUI(); }));
        syncCycleUI();

        // T-17 — load the user's own results; empty accounts see nothing new.
        const escText = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        client.getBillingUsageRecap().then(recap => {
            if (!recap || !recap.ok || !el.isConnected) return;
            const recapEl = el.querySelector('#upgrade-recap');
            if (recapEl && (recap.generated || recap.scheduled || recap.published)) {
                const lead = recap.days ? `In ${recap.days} day${recap.days === 1 ? '' : 's'} with Heist` : 'With Heist';
                const best = recap.best_post && recap.best_post.preview
                    ? `<div class="upgrade-recap__best">Best post${recap.best_post.platform ? ` (${escText(recap.best_post.platform)})` : ''}: &ldquo;${escText(recap.best_post.preview)}&hellip;&rdquo;${recap.best_post.engagement ? ` &mdash; ${recap.best_post.engagement} engagements` : ''}</div>`
                    : '';
                recapEl.innerHTML = `<div>${lead} you generated <strong>${recap.generated}</strong> post${recap.generated === 1 ? '' : 's'}, scheduled <strong>${recap.scheduled}</strong>, and published <strong>${recap.published}</strong>.</div>${best}`;
                recapEl.hidden = false;
            }
            if (recap.recommended_tier && tiers.some(t => t.id === recap.recommended_tier)) {
                recommendedTier = recap.recommended_tier;
                syncCycleUI();
            }
        }).catch(() => { /* recap is decoration — the paywall works without it */ });

        // Coupon
        const couponBtn = el.querySelector('#coupon-apply-btn');
        const couponInput = el.querySelector('#coupon-input');
        const couponMsg = el.querySelector('#coupon-msg');
        couponBtn.addEventListener('click', async () => {
            const code = couponInput.value.trim();
            if (!code) return;
            couponBtn.disabled = true;
            couponBtn.textContent = 'Applying...';
            couponMsg.textContent = '';
            try {
                const res = await client.discountRedeem(code);
                if (res.upgraded) {
                    couponMsg.style.color = 'var(--success)';
                    couponMsg.textContent = 'Coupon applied! Redirecting...';
                    setTimeout(() => { location.hash = '#home'; }, 1500);
                } else {
                    const pct = res.discount || res.percent || 0;
                    couponMsg.style.color = 'var(--success)';
                    couponMsg.textContent = `${pct}% off applied`;
                    window.__discountCode = code;
                }
            } catch (err) {
                couponMsg.style.color = 'var(--danger)';
                couponMsg.textContent = err.message || 'Invalid coupon';
            } finally {
                couponBtn.disabled = false;
                couponBtn.textContent = 'Apply';
            }
        });
    }
    unmount() { }
}
